import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  useCreateEnrollment,
  useSearchStudentByPhone,
} from '@/hooks/mutations/useEnrollmentMutations'
import {
  calcValidTill,
  cleanPhone,
  COMBO_TYPES,
  getCourseLabel,
  getProgramLabel,
  PACKAGES,
  PAYMENT_MODES,
  today,
} from '@fintree/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useActor } from '@/hooks/useActor'
import { useCourseAttempts } from '@/hooks/useCourseAttempts'
import { InstallmentPlanFields } from '@/components/enroll/InstallmentPlanFields'
import { COURSE_GROUPS, enrollmentFormModel, isNoAttemptNoPackageCourse, resolveCourseKeyForAttempts } from './enrollConstants'
import type { IndianFormInput, InstallmentFormEntry } from './enrollConstants'

export function IndianEnrollForm() {
  const navigate = useNavigate()
  const actor = useActor()
  const [duplicateInfo, setDuplicateInfo] = useState<{
    name: string | null
    course: string | null
    attempt: string | null
    validTill: string | null
    historyCount: number
    activeEnrollments: { course: string | null; attempt: string | null; program: string | null }[]
  } | null>(null)

  const form = useForm<IndianFormInput>({
    resolver: zodResolver(enrollmentFormModel.indian),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      city: '',
      course: '',
      comboType: '',
      package: '',
      attempt: '',
      customAttempt: '',
      enrollmentDate: today(),
      amount: undefined,
      currency: 'INR',
      paymentMode: '',
      razorpayPaymentId: '',
      installments: [],
    },
  })

  /* eslint-disable react-hooks/incompatible-library -- RHF watch(); React Compiler false positive; derived enroll UI state */
  const selectedCourse = form.watch('course')
  const selectedCombo = form.watch('comboType')
  const selectedAttempt = form.watch('attempt')
  const selectedCustomAttempt = form.watch('customAttempt')
  const selectedDate = form.watch('enrollmentDate')
  const revisionMonths = form.watch('revisionMonths')
  const selectedPaymentMode = form.watch('paymentMode')
  /* eslint-enable react-hooks/incompatible-library */
  const isRevisionCourse = selectedCourse.startsWith('REV_') || selectedCourse === 'REV_PACK'
  const isNoAttemptNoPackage = isNoAttemptNoPackageCourse(selectedCourse)
  const resolvedCourse = useMemo(() => {
    if (selectedCourse === 'COMBO' && selectedCombo) {
      return COMBO_TYPES[selectedCombo as keyof typeof COMBO_TYPES]?.startCourse ?? 'CFA_L1'
    }
    return selectedCourse
  }, [selectedCombo, selectedCourse])

  const courseKeyForAttempts = useMemo(
    () => resolveCourseKeyForAttempts(selectedCourse, selectedCombo),
    [selectedCourse, selectedCombo]
  )

  const { attemptOptions, isLoading: attemptsLoading } = useCourseAttempts(courseKeyForAttempts)
  const comboNeedsType = selectedCourse === 'COMBO' && !selectedCombo

  useEffect(() => {
    if (isNoAttemptNoPackage && !revisionMonths) {
      form.setValue('revisionMonths', 3)
    }
  }, [form, isNoAttemptNoPackage, revisionMonths])

  const resolvedAttempt = selectedAttempt === '__custom__' ? selectedCustomAttempt : selectedAttempt
  const resolvedProgram = isRevisionCourse || isNoAttemptNoPackage ? null : (form.watch('package') || null)
  const duplicateConflict = duplicateInfo?.activeEnrollments.some(
    (e) =>
      e.course === resolvedCourse &&
      (e.attempt || '') === (resolvedAttempt || '') &&
      (e.program || '') === (resolvedProgram || '')
  )
  const validTillPreview = calcValidTill(
    resolvedCourse,
    resolvedAttempt || undefined,
    selectedCombo || undefined,
    selectedDate,
    isNoAttemptNoPackage ? (revisionMonths ?? 3) : revisionMonths
  )

  const duplicateMutation = useSearchStudentByPhone({
    onSuccess: (result) => {
      if (!result.student) {
        setDuplicateInfo(null)
        return
      }
      setDuplicateInfo({
        name: result.student.name,
        course: result.student.course,
        attempt: result.student.attempt,
        validTill: result.student.valid_till,
        historyCount: result.enrollmentHistoryCount,
        activeEnrollments: result.activeEnrollments,
      })
    },
  })

  const mutation = useCreateEnrollment(actor, {
    onSuccess: (_, payload) => {
      navigate(`/students/${encodeURIComponent(payload.phone)}`)
    },
  })

  return (
    <form
      className="space-y-4 rounded-lg border border-border bg-card p-6"
      onSubmit={form.handleSubmit(async (values) => {
        const attempt = values.attempt === '__custom__' ? values.customAttempt : values.attempt
        mutation.mutate({
          name: values.name,
          phone: cleanPhone(values.phone),
          email: values.email || undefined,
          city: values.city || undefined,
          course: values.course === 'COMBO' ? 'CFA_L1' : values.course,
          comboType: values.course === 'COMBO' ? (values.comboType as keyof typeof COMBO_TYPES) : undefined,
          revisionMonths:
            values.course.startsWith('REV_') || values.course === 'REV_PACK' || isNoAttemptNoPackageCourse(values.course)
              ? isNoAttemptNoPackageCourse(values.course)
                ? (values.revisionMonths ?? 3)
                : values.revisionMonths
              : undefined,
          package:
            values.course.startsWith('REV_') || values.course === 'REV_PACK' || isNoAttemptNoPackageCourse(values.course)
              ? undefined
              : values.package,
          attempt: values.course === 'FM' || isNoAttemptNoPackageCourse(values.course) ? undefined : attempt,
          enrollmentDate: values.enrollmentDate,
          amount: values.amount,
          currency: values.currency,
          paymentMode: values.paymentMode || undefined,
          razorpayPaymentId: values.razorpayPaymentId || undefined,
          source: 'hub',
          installments: values.installments.length
            ? values.installments.map((inst: InstallmentFormEntry) => ({
                amount: inst.amount,
                dueDate: inst.dueDate,
                notes: inst.notes || null,
                phone: cleanPhone(values.phone),
              }))
            : undefined,
        })
      })}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div><Label>Full Name</Label><Input disabled={mutation.isPending} {...form.register('name')} /></div>
          <div>
            <Label>Phone</Label>
            <Input
              disabled={mutation.isPending}
              {...form.register('phone')}
              onBlur={(event) => {
                const phone = cleanPhone(event.target.value)
                if (phone) duplicateMutation.mutate(phone)
              }}
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              disabled={mutation.isPending}
              {...form.register('email', {
                onChange: () => {
                  mutation.reset()
                },
              })}
            />
          </div>
          <div><Label>City</Label><Input disabled={mutation.isPending} {...form.register('city')} /></div>
        </div>

        <div className="space-y-3">
          <div>
            <Label>Course</Label>
            <select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm" disabled={mutation.isPending} {...form.register('course')}>
              <option value="">Select course</option>
              {COURSE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option} value={option}>
                      {getCourseLabel(option)}
                    </option>
                  ))}
                </optgroup>
              ))}
              <optgroup label="Combo"><option value="COMBO">Combo Package</option></optgroup>
            </select>
          </div>
          {selectedCourse === 'COMBO' ? (
            <div>
              <Label>Combo Type</Label>
              <select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm" disabled={mutation.isPending} {...form.register('comboType')}>
                <option value="">Select combo type</option>
                <option value="L1_L2">CFA L1 + L2 (24 months)</option>
                <option value="L2_L3">CFA L2 + L3 (24 months)</option>
                <option value="L1_L2_L3">CFA L1 + L2 + L3 (36 months)</option>
              </select>
            </div>
          ) : null}
          {isRevisionCourse ? (
            <div>
              <Label>Revision Duration</Label>
              <select
                className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
                disabled={mutation.isPending}
                value={form.watch('revisionMonths') ?? ''}
                onChange={(event) => form.setValue('revisionMonths', Number(event.target.value) || undefined)}
              >
                <option value="">Select duration</option><option value="3">3 months</option><option value="6">6 months</option>
              </select>
            </div>
          ) : null}
          {isNoAttemptNoPackage ? (
            <div>
              <Label>Valid Till Duration (months)</Label>
              <Input
                type="number"
                min={1}
                disabled={mutation.isPending}
                value={revisionMonths ?? 3}
                onChange={(event) => form.setValue('revisionMonths', Number(event.target.value) || 3)}
              />
            </div>
          ) : null}
          {!isRevisionCourse && !isNoAttemptNoPackage ? <div><Label>Package</Label><select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm" disabled={mutation.isPending} {...form.register('package')}><option value="">Select package</option>{PACKAGES.map((pkg) => (
                <option key={pkg} value={pkg}>
                  {getProgramLabel(pkg)}
                </option>
              ))}</select></div> : null}
          <div>
            <Label>Attempt</Label>
            <select
              className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"
              disabled={mutation.isPending || selectedCourse === 'FM' || isNoAttemptNoPackage || comboNeedsType}
              {...form.register('attempt')}
            >
              <option value="">
                {selectedCourse === 'FM' || isNoAttemptNoPackage
                  ? 'Not required for this course'
                  : comboNeedsType
                    ? 'Select combo type first'
                    : attemptsLoading
                      ? 'Loading attempts…'
                      : 'Select attempt'}
              </option>
              {attemptOptions.map((attempt) => (
                <option key={attempt} value={attempt}>
                  {attempt}
                </option>
              ))}
              {selectedCourse && selectedCourse !== 'FM' && !isNoAttemptNoPackage && !comboNeedsType ? (
                <option value="__custom__">+ Add new attempt</option>
              ) : null}
            </select>
          </div>
          {selectedAttempt === '__custom__' && !isNoAttemptNoPackage ? <div><Label>Custom Attempt</Label><Input disabled={mutation.isPending} {...form.register('customAttempt')} placeholder="e.g. May_28" /></div> : null}
          <div><Label>Enrollment Date</Label><Input type="date" disabled={mutation.isPending} {...form.register('enrollmentDate')} /></div>
          <div>
            <Label>Currency</Label>
            <select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm" disabled={mutation.isPending} {...form.register('currency')}>
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <div>
            <Label>Amount Paid</Label>
            <Input type="text" inputMode="numeric" disabled={mutation.isPending} {...form.register('amount')} />
            {form.formState.errors.amount ? <p className="text-xs text-destructive">{form.formState.errors.amount.message}</p> : null}
          </div>
          <div>
            <Label>Payment Mode</Label>
            <select className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm" disabled={mutation.isPending} {...form.register('paymentMode')}>
              <option value="">Select mode</option>
              {PAYMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
            {form.formState.errors.paymentMode ? <p className="text-xs text-destructive">{form.formState.errors.paymentMode.message}</p> : null}
          </div>
          {selectedPaymentMode === 'Razorpay' ? (
            <div>
              <Label>Razorpay Payment ID (optional)</Label>
              <Input
                disabled={mutation.isPending}
                placeholder="pay_xxxxxxxxxxxxxxxx"
                {...form.register('razorpayPaymentId')}
              />
              {form.formState.errors.razorpayPaymentId ? (
                <p className="text-xs text-destructive">{form.formState.errors.razorpayPaymentId.message}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <InstallmentPlanFields
        values={form.watch('installments') ?? []}
        onChange={(values) => form.setValue('installments', values, { shouldValidate: true })}
        disabled={mutation.isPending}
      />

      {duplicateInfo ? (
        <div className={`rounded-lg border p-3 text-sm ${duplicateConflict ? 'border-red-300 bg-destructive/10 text-destructive' : 'border-fintree-amber/40 bg-fintree-amber/10 text-fintree-amber'}`}>
          {duplicateConflict ? (
            <>
              <p className="font-medium">This student already has an active enrollment for this course, attempt, and package.</p>
              <p>Submitting will fail. Use the Enrollment Change tab on the student profile to extend validity, retake, or upgrade package.</p>
            </>
          ) : (
            <>
              <p className="font-medium">This student already exists. Submitting will add a new enrollment record.</p>
              <p>Existing: {duplicateInfo.name ?? '—'} | {duplicateInfo.course ? getCourseLabel(duplicateInfo.course) : '—'} | {duplicateInfo.attempt ?? '—'} | Valid till: {duplicateInfo.validTill ?? '—'} | History count: {duplicateInfo.historyCount}</p>
            </>
          )}
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-background p-3 text-sm">
        <span className="text-muted-foreground">Valid Till Preview:</span> {validTillPreview ?? 'Not enough information'}
      </div>

      <Button type="submit" disabled={mutation.isPending}>{mutation.isPending ? 'Saving...' : 'Save enrollment'}</Button>
    </form>
  )
}
